import type { PropsWithChildren, ReactNode } from 'react';
import type React from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { Field, SmartFieldEvent } from '#src/app/shared/utils/payments';

import { StyleWrapper } from '../../components/StyleWrapper';

import { DlocalButton } from './DlocalButton';
import { DLocalSmartField } from './DLocalSmartField';
import type { DLocalValidationError } from './types';

type Props = {
	visible: boolean;
	handleSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
	error: DLocalValidationError;
	disabled: boolean;
	loading: boolean;
	useDonationLanguage: boolean;
	onCardExpiryFieldFocus: () => void;
	onCardCVCFieldFocus: () => void;
	updateError: (field: string, event: SmartFieldEvent) => void;
	onCardNumberFieldFocus: () => void;
	setPan: (field: Field | null) => void;
	nameInput?: ReactNode;
};

export const DLocalCreditCardForm: React.FC<PropsWithChildren<Props>> = ({
	visible,
	handleSubmit,
	children,
	error,
	disabled,
	loading,
	useDonationLanguage,
	onCardExpiryFieldFocus,
	onCardCVCFieldFocus,
	updateError,
	onCardNumberFieldFocus,
	setPan,
	nameInput,
}) => {
	const { translate } = useI18n();

	return (
		<form
			className={visible ? 'block' : 'hidden'}
			data-testid="dlocal-credit-card-form"
			onSubmit={handleSubmit}
			data-qa="dlocal-credit-card-form"
		>
			<div className="flex flex-col gap-4">
				{nameInput}
				<div className="relative">
					<div data-testid="credit-card-number" data-qa="dlocal-credit-card-form-card-number">
						<StyleWrapper
							label={translate('corgi.payments.stripe_elements.card_number')}
							errorMessage={error.card}
							onFocus={onCardNumberFieldFocus}
							onBlur={(event) => updateError('card', event as SmartFieldEvent)}
						>
							<DLocalSmartField type="pan" onFieldCreated={setPan} placeholder="4111 1111 1111 1111" />
						</StyleWrapper>
					</div>
					<div className="absolute top-0 right-0">
						<img
							alt={translate('corgi.payments.stripe_elements.card_image_alt_text')}
							src="https://assets.change.org/app-assets/victory_fund/credit-card-icons.png"
							height="20px"
							width="127px"
						/>
					</div>
				</div>
				<div className="flex gap-2">
					<div className="flex-1" data-testid="credit-card-expiry" data-qa="dlocal-credit-card-form-card-expiry">
						<StyleWrapper
							label={translate('corgi.payments.stripe_elements.expiration')}
							errorMessage={error.expire}
							onFocus={onCardExpiryFieldFocus}
							onBlur={(event) => updateError('expire', event as SmartFieldEvent)}
						>
							<DLocalSmartField type="expiration" />
						</StyleWrapper>
					</div>

					<div className="flex-1" data-testid="credit-card-cvc" data-qa="dlocal-credit-card-form-card-cvc">
						<StyleWrapper
							label={translate('corgi.payments.stripe_elements.cvc')}
							errorMessage={error.cvv}
							onFocus={onCardCVCFieldFocus}
							onBlur={(event) => updateError('cvv', event as SmartFieldEvent)}
						>
							<DLocalSmartField type="cvv" />
						</StyleWrapper>
					</div>
				</div>
				{children}
				<div>
					<DlocalButton disabled={disabled || loading} loading={loading} useDonationLanguage={useDonationLanguage} />
				</div>
			</div>
		</form>
	);
};
